import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled, { css } from 'styled-components'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { isEmpty } from 'ramda'
import { Box } from '@machobear/design-system'
import { Userpilot } from 'userpilot'
import { colors, zIndex, breakpoints } from '@/styles'
import ENV_VARS from '@/config'
import useProfile from '@/hooks/use-profile'
import useClickOutside from '@/hooks/use-click-outside'
import { formatPhone } from '@/utils/format-phone'
import { getImageUrl } from '@/utils/image'
import isVerified from '@/utils/is-verified'
import useMediaQuery from '@/hooks/use-media-query'
import { TextLink } from '@/components/button'
import { Avatar } from '@/components/avatar'
import { ParagraphMedium, ParagraphRegular } from '@/components/text'
import {
  LogoutIcon,
  SettingsIcon,
  SupportIcon,
  TeamIcon,
  RedCircleIcon,
  PricingIcon,
  BellIcon
} from '@/components/icons'
import VerifiedBadge from '@/components/verified-badge'
import { ParagraphSmall } from '@/components/text'

const { WEB_URL } = ENV_VARS

const style = css`
  font-weight: 500;
  color: ${colors.link};
  background: transparent;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 0.7;
  }
`

const iconStyle = css`
  position: relative;
  top: 1.5px;
  margin-left: 13px;
  margin-right: 9px;
  width: 13.33px;
  height: 13.33px;
  color: ${colors.grey56};
`

const DropdownWrapper = styled.div`
  position: ${({ forPromotion }) => (forPromotion ? 'absolute' : 'fixed')};
  display: flex;
  z-index: ${zIndex.modal};
  right: ${({ forPromotion }) => (forPromotion ? '0px' : '32px')};
  top: ${({ forPromotion }) => (forPromotion ? '50px' : '54px')};
  align-self: right;
  flex-direction: column;
  justify-content: center;
  align-items: left;
  width: 250px;
  margin-top: 4px;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey88};
  border-radius: 8px;
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
`

const DropDownHeader = styled.div`
  background-color: ${colors.background};
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px 8px 0px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px;
  gap: 0px;
  box-sizing: border-box;
  width: 100%;
  height: ${({ forPromotion }) => (forPromotion ? 'unset' : '168px')};
`

const BoldText = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  margin-top: 16px;
  color: ${colors.grey08};
`

const SmallText = styled(ParagraphSmall)`
  padding-top: 4px;
  width: 202px;
  color: ${colors.grey40};
  white-space: nowrap;
  text-align: center;
`

const Email = styled(ParagraphSmall)`
  padding-top: 4px;
  text-align: right;
  max-width: 120px;
  color: ${colors.grey40};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EmailDomain = styled(ParagraphSmall)`
  padding-top: 4px;
  color: ${colors.grey40};
`

const EmailWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const DropDownLink = styled(Link)`
  line-height: 32px;
  margin: 4px 4px 4px 4px;
  color: ${colors.grey24};
  :hover {
    background-color: ${colors.grey96};
    border-radius: 8px;
  }
`

const StyledProfileLink = styled(TextLink)`
  padding-top: 8px;
  ${style}
`

const TeamIconGrey = styled(TeamIcon)`
  margin-left: 13.33px;
  margin-right: 9.33px;
`

const TeamIconDisabled = styled(TeamIcon)`
  margin-left: 14px;
  margin-right: 4px;
  path {
    fill: ${colors.grey64};
  }
`

const ItemDisabled = styled(ParagraphRegular)`
  margin: 4px 0px 0px 4px;
  color: ${colors.grey64};
  line-height: 32px;
  cursor: not-allowed;
`

const PricingIconGrey = styled(PricingIcon)`
  ${iconStyle}
`

const SettingsIconGrey = styled(SettingsIcon)`
  ${iconStyle}
`

const SupportIconGrey = styled(SupportIcon)`
  ${iconStyle}
`

const LogoutIconGrey = styled(LogoutIcon)`
  position: relative;
  top: 1.5px;
  margin: 4px 10.67px 0px 14.67px;
`

const Line = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  height: 1px;
`

const CurrentPlanText = styled(SmallText)`
  margin-left: 12px;
  margin-top: 8px;
  text-align: left;
  color: ${colors.grey48};
`

const MediumText = styled(ParagraphMedium)`
  margin-left: 12px;
  margin-bottom: 12px;
  font-weight: 500;
  color: ${colors.grey16};
`

const AvatarWrapper = styled.div`
  position: relative;
  top: 0;
  left: 0;
`

const StyledRedCircleIcon = styled(RedCircleIcon)`
  position: absolute;
  left: 24px;
  z-index: 2;
`

const StyledLink = styled.a`
  float: right;
  padding-right: 12px;
  color: ${colors.link};
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 0.7;
  }
`

const TextDiv = styled.div`
  float: left;
  width: 134px;
`

const NameWrapper = styled.div`
  float: left;
  max-width: 166px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Dropdown = ({ isOpen, setIsOpen, forPromotion = false }) => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const data = useProfile()
  const location = useLocation()
  const isTenant = data.user_type === 'tenant'
  const verified = isVerified(data, data?.company_info)
  const Plans = {
    'essentials-plan-monthly': t('l.menu.view.essentials_free.button', 'Essentials (Free)'),
    'growth-plan-monthly': t('b.auth.create.growth.label', 'Growth'),
    'growth-plan-annual': t('b.auth.create.growth.label', 'Growth'),
    'business-plan-annual': t('b.auth.create.business.label', 'Business'),
    'business-plan-monthly': t('b.auth.create.business.label', 'Business'),
    'business-plan-special': t('b.auth.create.enterprise.label', 'Enterprise'),
    'growth-plan-monthly-coupon-wbrothers': t('b.auth.create.growth.label', 'Growth')
  }
  const currentPlan = data.company_info?.base_plan_name ?? 'essentials-plan-monthly'

  const currentLang = i18n.languages[0]
  const localizedWebUrl = currentLang === 'en' ? WEB_URL : `${WEB_URL}/${currentLang}`

  const close = () => setIsOpen(false)

  const ref = useClickOutside(close)

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const handleClick = () => {
    setIsOpen(open => !open)
  }

  const handlePricing = id => () => {
    Userpilot.track(id)
    setIsOpen(open => !open)
  }

  const logoutAndRedirect = async event => {
    event.preventDefault()
    await dispatch.auth.logout()
    history.replace(location.pathname.includes('/promotions/') ? location.pathname : '/')
  }

  const renderAvatar = customSize => {
    const { width, height } = customSize || {}
    return (
      <AvatarWrapper>
        {isEmpty(data.avatar) && isTenant && <StyledRedCircleIcon />}
        <Avatar
          right={8}
          height={height || 32}
          width={width || 32}
          backgroundColor={colors.white}
          border={true}
          location={getImageUrl(data.avatar?.aws_s3_key, { width: 64, height: 64 })}
          alt={data.first_name}
          showIcon={true}
          absolute={false}
          relative={true}
        />
      </AvatarWrapper>
    )
  }

  const getFullName = () => (
    <BoldText>
      <NameWrapper>{`${data.first_name} ${data.last_name} `}</NameWrapper>
      {verified && <VerifiedBadge type="id" width={16} height={16} bottom={-2.5} verified={true} />}
    </BoldText>
  )

  const renderDropDownHeader = () => {
    const fullEmail = data.username.split('@')
    const email = fullEmail[0]
    const emailDomain = fullEmail[1]
    const completion = data.completion_percentage
    return (
      <DropDownHeader>
        {renderAvatar()}
        {getFullName()}
        <EmailWrapper>
          <Email>{email}</Email>
          <EmailDomain>{`@${emailDomain}`}</EmailDomain>
        </EmailWrapper>
        <SmallText>{formatPhone(data.phone_number, data.phone_country_code_alpha)}</SmallText>
        <StyledProfileLink
          id="menu-manage-logged-profile"
          to={isTenant ? '/profile/basic-info' : '/profile'}
          onClick={handleClick}
        >
          {completion === 100 || !isTenant
            ? t('b.menu.view.view_profile.button', 'View Profile')
            : t('r.menu.view.finish_profile_button.label', {
                defaultValue: 'Finish Profile ({{ completion }}%)',
                completion
              })}
        </StyledProfileLink>
      </DropDownHeader>
    )
  }

  const renderPromotionDropDownHeader = () => {
    const fullEmail = data.username.split('@')
    const email = fullEmail[0]
    const emailDomain = fullEmail[1]
    return (
      <DropDownHeader forPromotion={forPromotion}>
        {renderAvatar({ width: 43, height: 43 })}
        {getFullName()}
        <EmailWrapper>
          <Email>{email}</Email>
          <EmailDomain>{`@${emailDomain}`}</EmailDomain>
        </EmailWrapper>
        <SmallText>{formatPhone(data.phone_number, data.phone_country_code_alpha)}</SmallText>
      </DropDownHeader>
    )
  }

  const renderCurrentPlan = () => {
    const planText =
      currentPlan === 'growth-plan-monthly' ||
      currentPlan === 'growth-plan-annual' ||
      currentPlan === 'essentials-plan-monthly'
        ? t('l.menu.view.upgrade.button', 'Upgrade')
        : t('l.menu.view.view_plan.button', 'View Plan')
    return (
      <>
        <CurrentPlanText>{t('l.plan_settings.current_plan.label', 'Current Plan')}</CurrentPlanText>
        <MediumText>
          <TextDiv>{Plans[currentPlan]}</TextDiv>
          <StyledLink
            href={`${localizedWebUrl}/pricing`}
            rel="noopener noreferrer"
            onClick={handlePricing('app_upgrade_menu_tab')}
          >
            {planText}
          </StyledLink>
        </MediumText>
        <Line />
      </>
    )
  }

  const renderTeamOption = () => (
    <>
      {!isTenant && currentPlan === 'essentials-plan-monthly' ? (
        <ItemDisabled>
          <TeamIconDisabled /> {t('l.menu.view.team.link', 'Team')}
          <StyledLink
            href={`${localizedWebUrl}/pricing`}
            rel="noopener noreferrer"
            onClick={handlePricing('app_upgrade_menu_tab_teams')}
          >
            {t('l.menu.view.upgrade.button', 'Upgrade')}
          </StyledLink>
        </ItemDisabled>
      ) : (
        <DropDownLink id="menu-manage-logged-team" to="/manage/users" onClick={handleClick}>
          <TeamIconGrey />
          {t('l.menu.view.team.link', 'Team')}
        </DropDownLink>
      )}
    </>
  )

  const renderSavedAlertsOption = () => (
    <DropDownLink
      as="a"
      id="lakshimi-saved-alerts-click"
      href={`${localizedWebUrl}/saved-alerts`}
      onClick={handleClick}
    >
      <Box display="flex">
        <Box display="flex" alignItems="center" marginLeft="xsmall" marginRight="xxsmall">
          <BellIcon color={colors.grey56} />
        </Box>
        {t('b.sidebar.view.saved_alerts.link', 'Saved Alerts')}
      </Box>
    </DropDownLink>
  )

  const renderPricingOption = () => (
    <DropDownLink
      as="a"
      id="menu-manage-logged-pricing"
      href={`${localizedWebUrl}/pricing`}
      onClick={handlePricing('app_upgrade_menu_tab')}
    >
      <PricingIconGrey />
      {t('b.sidebar.view.pricing.link', 'Pricing')}
    </DropDownLink>
  )

  const renderSettingsOption = () => (
    <DropDownLink
      id="menu-manage-logged-settings"
      to={
        isMobile ? '/settings' : isTenant ? '/settings/payment-settings' : '/settings/plan-settings'
      }
      onClick={handleClick}
    >
      <SettingsIconGrey />
      {t('b.sidebar.view.settings.link', 'Settings')}
    </DropDownLink>
  )

  const renderSupportOption = () => (
    <DropDownLink id="menu-manage-logged-support" to="/support" onClick={handleClick}>
      <SupportIconGrey />
      {t('b.sidebar.view.support.link', 'Support')}
    </DropDownLink>
  )

  const renderLogoutOption = () => {
    return (
      <>
        <Line />
        <DropDownLink id="menu-manage-logged-logout" to="/" onClick={logoutAndRedirect}>
          <Box paddingY={forPromotion ? 'xsmall' : 'none'}>
            <LogoutIconGrey />
            {t('b.menu.view.logout.link', 'Logout')}
          </Box>
        </DropDownLink>
      </>
    )
  }

  const renderTenantMenu = () => (
    <>
      {renderDropDownHeader()}
      {renderSettingsOption()}
      {renderSupportOption()}
      {renderLogoutOption()}
    </>
  )

  const renderLandlordMenu = () => (
    <>
      {renderDropDownHeader()}
      {renderCurrentPlan()}
      {renderTeamOption()}
      {renderSavedAlertsOption()}
      {renderPricingOption()}
      {renderSettingsOption()}
      {renderSupportOption()}
      {renderLogoutOption()}
    </>
  )
  const renderPromotionMenu = () => (
    <>
      {renderPromotionDropDownHeader()}
      {renderLogoutOption()}
    </>
  )

  return (
    <DropdownWrapper ref={ref} forPromotion={forPromotion}>
      {forPromotion ? renderPromotionMenu() : isTenant ? renderTenantMenu() : renderLandlordMenu()}
    </DropdownWrapper>
  )
}

export default Dropdown
